import __ruffleWasm0 from "./ruffle_web_bg.wasm";
import __ruffleWasm1 from "./ruffle_web-wasm_mvp_bg.wasm";
/**
 * Conditional ruffle loader
 */
import { bulkMemory, simd, saturatedFloatToInt, signExtensions, referenceTypes, } from "wasm-feature-detect";
import { setPolyfillsOnLoad } from "./js-polyfills.js";
import { publicPath } from "./public-path.js";
import { internalSourceApi } from "./internal/internal-source-api.js";
/**
 * Load ruffle from an automatically-detected location.
 *
 * This function returns a new instance of Ruffle and downloads it every time.
 * You should not use it directly; this module will memoize the resource
 * download.
 *
 * @param progressCallback The callback that will be run with Ruffle's download progress.
 * @returns A ruffle-builder constructor that may be used to create new RuffleInstanceBuilder
 * instances.
 */
async function fetchRuffle(progressCallback) {
    // Apply some pure JavaScript polyfills to prevent conflicts with external
    // libraries, if needed.
    setPolyfillsOnLoad();
    // NOTE: Keep this list in sync with $RUSTFLAGS in the CI build config!
    const extensionsSupported = (await Promise.all([
        bulkMemory(),
        simd(),
        saturatedFloatToInt(),
        signExtensions(),
        referenceTypes(),
    ])).every(Boolean);
    // @ts-expect-error TS2367 ruffle_web-wasm_mvp gets replaced in set_version.ts.
    // ruffle_web-wasm_mvp is "ruffle_web-wasm_mvp" if this is a dual-wasm build.
    // We don't say we're falling back if we have only an extension build.
    if (!extensionsSupported && "ruffle_web-wasm_mvp" === "ruffle_web-wasm_mvp") {
        console.log("Some WebAssembly extensions are NOT available, falling back to the vanilla WebAssembly module");
    }
    // Easy "on first load": just set it to something else after the call.
    internalSourceApi.options.onFirstLoad?.();
    internalSourceApi.options.onFirstLoad = () => { };
    // Note: The argument passed to import() has to be a simple string literal,
    // otherwise some bundler will get confused and won't include the module?
    const { default: init, RuffleInstanceBuilder, ZipWriter, } = await (extensionsSupported
        ? // @ts-expect-error TS2307 TypeScript compiler is trying to do the import.
            import("./ruffle_web.js")
        : // @ts-expect-error TS2307 TypeScript compiler is trying to do the import.
            import("./ruffle_web-wasm_mvp.js"));
    let response;
    let wasmUrl;
    if (typeof import.meta.url === "string") {
        // ES module consumers (e.g. the Vite-built demo): bundlers recognise
        // this pattern and emit the .wasm as an asset. The path must stay a
        // string literal for them to do so.
        wasmUrl = extensionsSupported
            ? new URL(__ruffleWasm0, import.meta.url)
            : new URL(__ruffleWasm1, import.meta.url);
    }
    else {
        // IIFE builds (extension, selfhosted): import.meta.url is defined away
        // by esbuild, and the .wasm sits next to the script.
        const baseUrl = new URL(publicPath((window.RufflePlayer?.config ?? {})), document.baseURI);
        wasmUrl = extensionsSupported
            ? new URL(__ruffleWasm0, baseUrl)
            : new URL(__ruffleWasm1, baseUrl);
    }
    const wasmResponse = await fetch(wasmUrl);
    // The Pale Moon browser lacks full support for ReadableStream.
    // However, ReadableStream itself is defined.
    const readableStreamProperlyDefined = typeof ReadableStreamDefaultController === "function";
    if (progressCallback && readableStreamProperlyDefined) {
        const contentLength = wasmResponse?.headers?.get("content-length") || "";
        let bytesLoaded = 0;
        // Use parseInt rather than Number so the empty string is coerced to NaN instead of 0
        const bytesTotal = parseInt(contentLength);
        response = new Response(new ReadableStream({
            async start(controller) {
                const reader = wasmResponse.body?.getReader();
                if (!reader) {
                    throw "Response had no body";
                }
                progressCallback(bytesLoaded, bytesTotal);
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) {
                        break;
                    }
                    if (value?.byteLength) {
                        bytesLoaded += value?.byteLength;
                    }
                    controller.enqueue(value);
                    progressCallback(bytesLoaded, bytesTotal);
                }
                controller.close();
            },
        }), wasmResponse);
    }
    else {
        response = wasmResponse;
    }
    await init({ module_or_path: response });
    return [RuffleInstanceBuilder, ZipWriter];
}
let nativeConstructors = null;
/**
 * Obtain an instance of `Ruffle`.
 *
 * This function returns a promise which yields a new `RuffleInstanceBuilder` asynchronously.
 *
 * @param progressCallback The callback that will be run with Ruffle's download progress.
 * @returns A ruffle instance builder.
 */
export async function createRuffleBuilder(progressCallback) {
    if (nativeConstructors === null) {
        nativeConstructors = fetchRuffle(progressCallback);
    }
    const constructors = await nativeConstructors;
    return [new constructors[0](), () => new constructors[1]()];
}
